import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { timeAgo } from "@/lib/ui";

export default function NotificationBell({ id = "bell" }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const go = async (n) => {
    setOpen(false);
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    load();
    navigate(n.link || "/requests");
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative w-10 h-10 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 active:bg-slate-100" data-testid={`${id}-button`} aria-label="Notifications">
          <Bell className="w-5 h-5" />
          {data.unread > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`${id}-count`}>
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[calc(100vw-1.5rem)] max-w-sm p-0" data-testid={`${id}-panel`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <p className="text-sm font-semibold text-slate-900">Notifications</p>
          {data.unread > 0 && <button onClick={readAll} className="text-xs text-indigo-600 hover:underline" data-testid={`${id}-read-all`}>Mark all read</button>}
        </div>
        <div className="max-h-[60vh] overflow-y-auto scroll-thin">
          {data.items.map((n) => (
            <button key={n.id} onClick={() => go(n)} className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 flex gap-2.5 ${n.read ? "" : "bg-indigo-50/40"}`} data-testid={`notification-${n.id}`}>
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-indigo-500"}`} />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-900">{n.title}</span>
                <span className="block text-xs text-slate-500 mt-0.5 break-words">{n.body}</span>
                <span className="block text-[11px] text-slate-400 mt-1">{timeAgo(n.created_at)}</span>
              </span>
            </button>
          ))}
          {data.items.length === 0 && <p className="text-sm text-slate-400 text-center py-8">You're all caught up.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
